import { CONFIG, HackId, WORLD } from '../../common/src/data';
import { GameMap, TILE } from '../../common/src/map';
import type { Snapshot, VehicleSnap } from '../../common/src/protocol';
import { DRIVER_PRIORITY, TargetKind, defaultHack, hacksFor } from '../../common/src/hacks';
import { angleDiff, dist } from '../../common/src/util';

export interface HackTarget { key: string; kind: TargetKind; x: number; y: number; npcKind?: string; raised?: boolean; label: string }
const MAX_RANGE = Math.max(CONFIG.hacks.short_circuit.range, CONFIG.hacks.sensor_jam.range, CONFIG.hacks.remote_override.range);

export function listTargets(s: Snapshot, map: GameMap, px: number, py: number, range = MAX_RANGE + 40): HackTarget[] {
  const out: HackTarget[] = [];
  const add = (t: HackTarget) => { if (dist(px, py, t.x, t.y) <= range) out.push(t); };
  map.intersections.forEach((it, i) => add({ key: `sig:${i}`, kind: 'sig', x: it.cx, y: it.cy, label: 'Traffic signal' }));
  WORLD.barriers.forEach((b, i) => add({ key: `bar:${i}`, kind: 'bar', x: (b.x + b.w / 2) * TILE, y: (b.y + b.h / 2) * TILE, raised: !!s.inf.bar[i], label: b.name }));
  WORLD.cameras.forEach((c, i) => { if (!s.inf.cam[i]?.[1]) add({ key: `cam:${i}`, kind: 'cam', x: c.x * TILE, y: c.y * TILE, label: 'Security camera' }); });
  WORLD.fixtures.forEach((f, i) => { if (!(s.inf.fix[i] > 0)) add({ key: `fix:${i}`, kind: 'fix', x: f.x * TILE, y: f.y * TILE, label: 'Transformer box' }); });
  WORLD.vents.forEach((v, i) => { if (!(s.inf.vent[i] > 0)) add({ key: `vent:${i}`, kind: 'vent', x: v.x * TILE, y: v.y * TILE, label: 'Steam vent' }); });
  for (const v of s.v) if (!(v.f & 16) && v.o.every(o => o === 0)) add({ key: `veh:${v.id}`, kind: 'veh', x: v.x, y: v.y, label: 'Unoccupied vehicle' });
  for (const n of s.n) if (n.k === 'drone' || n.k === 'elite') add({ key: `npc:${n.id}`, kind: 'npc', x: n.x, y: n.y, npcKind: n.k, label: n.k === 'drone' ? 'Security drone' : 'Crimson Protocol operative' });
  return out;
}
export function hackRange(h: HackId) { return (CONFIG.hacks[h] as { range: number }).range; }
export function applicable(t: HackTarget) { return hacksFor(t.kind, t.npcKind); }

/** Driver assist: one stable target, preferring useful infrastructure ahead of the car. */
export class DriverAssist {
  current: string | null = null; heldSince = 0;
  pick(targets: HackTarget[], v: VehicleSnap, now: number, reserved: Record<string, number>): HackTarget | null {
    const speed = Math.hypot(v.vx, v.vy);
    const heading = speed > 20 ? Math.atan2(v.vy, v.vx) : v.a;
    const score = (t: HackTarget) => {
      const h = defaultHack(t.kind, t.npcKind); if (!h) return -1;
      if (reserved[t.key]) return -1;
      const d = dist(v.x, v.y, t.x, t.y); if (d > hackRange(h)) return -1;
      if (t.kind === 'veh' && d < 30) return -1;
      const ang = Math.abs(angleDiff(heading, Math.atan2(t.y - v.y, t.x - v.x)));
      const ahead = ang < 1.0 ? 1.6 : ang < 1.8 ? 0.8 : 0.35;
      return DRIVER_PRIORITY[t.kind] * ahead * (1.4 - d / 300);
    };
    let best: HackTarget | null = null, bs = 0;
    for (const t of targets) { const sc = score(t); if (sc > bs) { bs = sc; best = t; } }
    const cur = targets.find(t => t.key === this.current);
    const cs = cur ? score(cur) : -1;
    // hysteresis: keep the current one unless it became invalid, or a much better one has appeared after a minimum hold time
    if (cur && cs > 0 && (!best || best.key === cur.key || bs < cs * 1.35 || now - this.heldSince < 0.8)) return cur;
    if (best?.key !== this.current) { this.current = best?.key ?? null; this.heldSince = now; }
    return best;
  }
}
